import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { Sparkles, Loader2, RefreshCw } from 'lucide-react'
import { supportChatsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useTenantDirectory } from '../lib/tenantDirectory'
import { toTenantModel, tenantView, healthChecks, makeMatcher, formatRelative, formatDate } from '../lib/support'
import { Dot, Tag, PageHeader, SearchInput, Segmented, Spinner, ErrorBanner } from '../components/ui'
import { ChatTranscript, markdownComponents } from '../components/ChatTranscript'

// Support chats inbox: list | transcript | context, plus the AI overview card.
// Selected chat lives in ?chat= so Today and the palette can deep-link.

const TYPES = [
  { value: 'all', label: 'Todas' },
  { value: 'tenant', label: 'Estúdio' },
  { value: 'public', label: 'Públicas' },
]

const isoDay = (ms) => new Date(ms).toISOString().split('T')[0]
// 'YYYY-MM-DD' → 'DD/MM/YYYY' without a timezone shift.
const brDay = (day) => (day ? day.split('-').reverse().join('/') : '')
const firstUserMessage = (chat) => chat?.messages?.find((m) => m.role === 'user')?.content || ''

/** The overview is markdown with `## Section` headings; each section becomes a tile. */
const splitSections = (markdown = '') => {
  const parts = markdown.split(/^##\s+/m)
  const intro = parts.shift().trim()
  const sections = parts.map((p) => {
    const [title, ...rest] = p.split('\n')
    return { title: title.trim(), body: rest.join('\n').trim() }
  })
  return { intro, sections }
}

const AiOverviewCard = ({ type, chatTotal, dateFrom, dateTo }) => {
  const { isOwner } = useAuth()
  const [reports, setReports] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    setError('')
    supportChatsAPI
      .getReports({ type })
      .then((res) => {
        const list = res.data.data || []
        setReports(list)
        setSelectedId(list[0]?.id || '')
      })
      .catch(() => setReports([]))
      .finally(() => setLoading(false))
  }, [type])

  const generate = async () => {
    setGenerating(true)
    setError('')
    try {
      const res = await supportChatsAPI.generateOverview({
        type,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      })
      const report = res.data.data
      setReports((prev) => [report, ...prev])
      setSelectedId(report.id)
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error?.message || 'Falha ao gerar a visão geral')
    } finally {
      setGenerating(false)
    }
  }

  const report = reports.find((r) => r.id === selectedId)
  const { intro, sections } = useMemo(() => splitSections(report?.overview), [report])

  return (
    <section className="card flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="flex-1 text-sm">Visão geral por IA · conversas {type === 'public' ? 'públicas' : 'de estúdios'}</h4>
        {report && (
          <span className="text-xs text-admin-500">
            Gerada em {formatDate(report.created_at)} · {report.chat_count} conversas
            {report.date_from && report.date_to ? ` · ${brDay(report.date_from)} – ${brDay(report.date_to)}` : ''}
          </span>
        )}
        {reports.length > 1 && (
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="input w-auto"
            aria-label="Selecionar relatório anterior"
          >
            {reports.map((r, i) => (
              <option key={r.id} value={r.id}>
                {i === 0 ? 'Mais recente — ' : ''}
                {formatDate(r.created_at)} · {r.chat_count} conversas
              </option>
            ))}
          </select>
        )}
        {isOwner && (
          <button type="button" className="btn btn-primary" onClick={generate} disabled={generating || chatTotal === 0}>
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {report ? 'Nova análise' : 'Gerar visão geral'}
          </button>
        )}
      </div>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {generating ? (
        <p className="flex items-center gap-2 py-4 text-[13px] text-admin-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Analisando {chatTotal} conversas…
        </p>
      ) : loading ? (
        <Spinner className="py-6" />
      ) : !report ? (
        <p className="text-[13px] text-admin-600">
          Ainda não há visão geral para conversas {type === 'public' ? 'públicas' : 'de estúdios'}.{isOwner ? '' : ' Um proprietário pode gerar uma.'}
        </p>
      ) : (
        <>
          {intro && (
            <div className="text-[13px]">
              <ReactMarkdown components={markdownComponents}>{intro}</ReactMarkdown>
            </div>
          )}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3 text-[13px]">
            {sections.map((s) => (
              <div key={s.title} className="flex flex-col gap-0.5 rounded-md bg-admin-50 p-3">
                <span className="font-bold">{s.title}</span>
                <ReactMarkdown components={markdownComponents}>{s.body}</ReactMarkdown>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

export const SupportAnalysisPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { tenants } = useTenantDirectory()
  const [type, setType] = useState('all')
  const [query, setQuery] = useState('')
  const [chats, setChats] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [insightsOpen, setInsightsOpen] = useState(false)
  const [dateFrom, setDateFrom] = useState(isoDay(Date.now() - 30 * 86400000))
  const [dateTo, setDateTo] = useState(isoDay(Date.now()))
  const [selectedChat, setSelectedChat] = useState(null)

  const selectedId = searchParams.get('chat')
  const selectChat = (id) => {
    const next = new URLSearchParams(searchParams)
    next.set('chat', id)
    setSearchParams(next, { replace: true })
  }

  const loadChats = useCallback(
    async (page = 1) => {
      setLoading(true)
      setError('')
      try {
        const params = { page, per_page: 30 }
        if (type !== 'all') params.type = type
        if (dateFrom) params.date_from = dateFrom
        if (dateTo) params.date_to = dateTo
        const res = await supportChatsAPI.getAll(params)
        setChats(res.data.data || [])
        setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 })
      } catch (err) {
        setChats([])
        setError(`Não foi possível carregar as conversas de suporte: ${err.response?.data?.error?.message || err.message}`)
      } finally {
        setLoading(false)
      }
    },
    [type, dateFrom, dateTo]
  )

  useEffect(() => {
    loadChats(1)
  }, [loadChats])

  // Deep-linked chats may be outside the current page/filter — fetch them directly.
  useEffect(() => {
    if (!selectedId) {
      setSelectedChat(null)
      return
    }
    const inList = chats.find((c) => c.id === selectedId)
    if (inList) {
      setSelectedChat(inList)
      return
    }
    let cancelled = false
    supportChatsAPI
      .getOne(selectedId)
      .then((res) => !cancelled && setSelectedChat(res.data.data || res.data))
      .catch(() => !cancelled && setSelectedChat(null))
    return () => {
      cancelled = true
    }
  }, [selectedId, chats])

  const tenantById = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants])
  const has = makeMatcher(query)
  const list = chats.filter((c) =>
    has(
      c.user_name,
      c.user_email,
      tenantById[c.tenant_id]?.name,
      c.origin,
      ...(c.messages || []).map((m) => m.content)
    )
  )

  const chat = selectedChat || (!selectedId ? list[0] : null)
  const chatTenant = chat?.tenant_id ? tenantById[chat.tenant_id] : null
  const ctxView = chatTenant ? tenantView(toTenantModel(chatTenant)) : null

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Conversas de suporte"
        subtitle={`${pagination.total} ${pagination.total === 1 ? 'conversa' : 'conversas'} · ${brDay(dateFrom)} – ${brDay(dateTo)}`}
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => setInsightsOpen((o) => !o)}>
            <Sparkles className="h-4 w-4" />
            {insightsOpen ? 'Ocultar visão geral por IA' : 'Visão geral por IA'}
          </button>
        }
      />

      {insightsOpen && (
        <AiOverviewCard
          type={type === 'public' ? 'public' : 'tenant'}
          chatTotal={pagination.total}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="flex flex-wrap items-start gap-4">
        {/* List */}
        <section className="card flex max-w-full flex-[1_1_240px] flex-col gap-2 p-3 sm:max-w-[300px]">
          <Segmented options={TYPES} value={type} onChange={setType} label="Tipo de conversa" />
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Buscar mensagens, pessoas, estúdios"
            aria-label="Pesquisar conversas"
            inputClassName="bg-admin-50"
          />
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Data inicial"
              className="input min-w-0 px-2 text-xs"
            />
            <span className="text-xs text-admin-500">–</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="Data final"
              className="input min-w-0 px-2 text-xs"
            />
            <button
              type="button"
              className="btn btn-ghost flex-none px-1.5"
              onClick={() => loadChats(pagination.page)}
              aria-label="Atualizar conversas"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
          {loading ? (
            <Spinner />
          ) : list.length === 0 ? (
            <p className="px-1 py-3 text-[13px] text-admin-600">Nenhuma conversa encontrada.</p>
          ) : (
            list.map((c) => {
              const isPublic = c.chat_type === 'public'
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectChat(c.id)}
                  aria-current={chat?.id === c.id ? 'true' : undefined}
                  className={`flex flex-col gap-0.5 rounded-md px-3 py-2.5 text-left hover:bg-admin-50 ${
                    chat?.id === c.id ? 'bg-admin-50' : ''
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate font-semibold">
                      {c.user_name || (isPublic ? 'Visitante da documentação' : 'Usuário desconhecido')}
                    </span>
                    <span className="whitespace-nowrap text-[11px] text-admin-500">{formatRelative(c.created_at)}</span>
                  </span>
                  <span className="text-xs text-admin-600">
                    {isPublic ? `Pública${c.origin ? ` · ${c.origin}` : ''}` : tenantById[c.tenant_id]?.name || 'Estúdio'}
                  </span>
                  {firstUserMessage(c) && <span className="truncate text-xs text-admin-600">“{firstUserMessage(c)}”</span>}
                </button>
              )
            })
          )}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between border-t border-admin-100 pt-2 text-xs text-admin-600">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={pagination.page <= 1}
                onClick={() => loadChats(pagination.page - 1)}
              >
                Anterior
              </button>
              <span>
                {pagination.page} / {pagination.pages}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={pagination.page >= pagination.pages}
                onClick={() => loadChats(pagination.page + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </section>

        {/* Transcript */}
        <section className="card flex min-h-[480px] min-w-0 flex-[999_1_320px] flex-col gap-2 p-4">
          {chat ? (
            <>
              <div className="flex items-center gap-2.5 border-b border-admin-200 pb-2">
                <div className="flex flex-1 flex-col leading-snug">
                  <span className="text-[15px] font-bold">
                    {chat.user_name || (chat.chat_type === 'public' ? 'Visitante da documentação' : 'Usuário desconhecido')}
                  </span>
                  <span className="text-xs text-admin-600">
                    {[chat.user_email, formatRelative(chat.created_at), `${chat.message_count} mensagens`]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                <Tag tone={chat.chat_type === 'public' ? 'muted' : 'ok'}>
                  {chat.chat_type === 'public' ? 'Pública' : 'Estúdio'}
                </Tag>
              </div>
              <ChatTranscript messages={chat.messages || []} showRole maxWidth="max-w-[78%]" />
            </>
          ) : (
            <p className="m-auto text-[13px] text-admin-600">
              {loading ? 'Carregando…' : selectedId ? 'Não foi possível carregar esta conversa.' : 'Selecione uma conversa.'}
            </p>
          )}
        </section>

        {/* Context */}
        <aside className="card flex flex-[1_1_240px] flex-col gap-3 p-4">
          <span className="micro-label">Contexto</span>
          {!chat ? (
            <p className="text-[13px] text-admin-600">—</p>
          ) : ctxView ? (
            <div className="flex flex-col gap-2">
              <span className="text-base font-semibold">{ctxView.model.name}</span>
              <div className="flex flex-wrap gap-1.5">
                <Tag tone="muted">{ctxView.model.plan}</Tag>
                <Tag tone={ctxView.billing.tone}>{ctxView.billing.label}</Tag>
              </div>
              {healthChecks(ctxView).map((c) => (
                <div key={c.label} className="grid grid-cols-[8px_96px_minmax(0,1fr)] items-center gap-2 text-xs">
                  <Dot tone={c.tone} />
                  <span className="text-admin-600">{c.label}</span>
                  <span className="truncate" title={c.value}>
                    {c.value}
                  </span>
                </div>
              ))}
              <button type="button" className="btn btn-primary mt-1" onClick={() => navigate(`/tenants/${ctxView.model.id}`)}>
                Abrir estúdio
              </button>
            </div>
          ) : chat.chat_type === 'public' ? (
            <div className="flex flex-col gap-1.5 text-[13px]">
              <span>Visitante anônimo do site de documentação.</span>
              {chat.origin && <span className="text-admin-600">Origem: {chat.origin}</span>}
              <span className="mono break-all text-xs text-admin-600">Sessão {chat.session_id}</span>
            </div>
          ) : (
            <p className="text-[13px] text-admin-600">Detalhes do estúdio indisponíveis.</p>
          )}
        </aside>
      </div>
    </div>
  )
}
